import React, { useState, useEffect } from 'react';
import {
	Box,
	Paper,
	Typography,
	LinearProgress,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
	Grid,
	Button as MuiButton,
} from '@mui/material';
import {
	CheckCircle as CheckCircleIcon,
	PendingActions as PendingIcon,
	Download as DownloadIcon,
} from '@mui/icons-material';
import {
	OnboardingEmployeeDocument,
	getOnboardingEmployeeDocuments,
	getOnboardingDocuments,
	extractIdFromIri,
	OnboardingRecord,
	approveOnboarding,
	rejectOnboarding,
	resetOnboarding,
} from 'core/API/onboarding';
import { IOnboardingDocument } from 'core/interfaces/onboardingInterface';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { Button } from '../../../components/ui/button';
import style from './OnboardingDashboard.module.scss';
import { capitalizeWords } from '../../../shared/utils/stringUtils';

interface EmployeeOnboardingReviewProps {
	record: OnboardingRecord;
	onBack: () => void;
	onRefresh?: () => Promise<void>;
}

export default function EmployeeOnboardingReview({
	record,
	onBack,
	onRefresh,
}: EmployeeOnboardingReviewProps): JSX.Element {
	const [isLoading, setIsLoading] = useState(true);
	const [isProcessing, setIsProcessing] = useState(false);
	const [employeeDocuments, setEmployeeDocuments] = useState<OnboardingEmployeeDocument[]>([]);
	const [documentTemplates, setDocumentTemplates] = useState<IOnboardingDocument[]>([]);
	const { showToast } = useToastContext();

	useEffect(() => {
		fetchOnboardingProgress();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [record.id]);

	const fetchOnboardingProgress = async () => {
		setIsLoading(true);
		try {
			// Fetch employee documents and templates
			const [employeeDocs, templates] = await Promise.all([
				getOnboardingEmployeeDocuments(record.id),
				getOnboardingDocuments(),
			]);

			setEmployeeDocuments(employeeDocs);
			setDocumentTemplates(templates);

			// Note: Phase two status is now embedded in the record.phaseTwoStatuses
			// No need to fetch it separately
		} catch (error) {
			console.error('Error fetching onboarding progress:', error);
			showToast('error', 'Failed to load onboarding progress');
		} finally {
			setIsLoading(false);
		}
	};

	const handleDownloadDocument = (doc: OnboardingEmployeeDocument) => {
		const url = `${process.env.REACT_APP_IMAGE_URL}/onboarding_employee/${doc.filePath}`;
		const link = document.createElement('a');
		link.href = url;
		link.download = doc.filePath.split('/').pop() || 'document.pdf';
		link.target = '_blank';
		link.click();
		showToast('success', 'Document download started');
	};

	const handleApprove = async () => {
		if (!window.confirm('Are you sure you want to approve this onboarding?')) {
			return;
		}

		setIsProcessing(true);
		try {
			await approveOnboarding(record.id);
			showToast('success', 'Onboarding approved successfully');
			if (onRefresh) await onRefresh();
			onBack();
		} catch (error) {
			console.error('Error approving onboarding:', error);
			showToast('error', 'Failed to approve onboarding');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleReject = async () => {
		if (!window.confirm('Are you sure you want to reject this onboarding? The employee will be notified.')) {
			return;
		}

		setIsProcessing(true);
		try {
			await rejectOnboarding(record.id);
			showToast('success', 'Onboarding rejected successfully');
			if (onRefresh) await onRefresh();
			onBack();
		} catch (error) {
			console.error('Error rejecting onboarding:', error);
			showToast('error', 'Failed to reject onboarding');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleReset = async () => {
		if (!window.confirm('Are you sure you want to reset this onboarding? The employee will need to fill out the forms again.')) {
			return;
		}

		setIsProcessing(true);
		try {
			await resetOnboarding(record.id);
			showToast('success', 'Onboarding reset successfully');
			if (onRefresh) await onRefresh();
			onBack();
		} catch (error) {
			console.error('Error resetting onboarding:', error);
			showToast('error', 'Failed to reset onboarding');
		} finally {
			setIsProcessing(false);
		}
	};

	// Map employee documents to templates
	// If phaseTwoStatuses is available, use it for more accurate status tracking
	const documentsWithStatus = documentTemplates.map((template) => {
		const uploadedDoc = employeeDocuments.find((doc) => {
			const templateId = extractIdFromIri(doc.template);
			return templateId === template.id;
		});

		// Check phase two status for this document if available
		let status = 'pending';
		if (record.phaseTwoStatuses?.progress?.documents) {
			const docStatus = record.phaseTwoStatuses.progress.documents.find(
				(d) => d.template_id === template.id
			);
			if (docStatus) {
				status = docStatus.status;
			}
		}

		// Fallback: if no status from API, check if document was uploaded
		const completed = status === 'completed' || status === 'approved' || !!uploadedDoc;

		return {
			template,
			uploaded: completed,
			uploadedDocument: uploadedDoc,
			status,
		};
	});

	const completedCount = documentsWithStatus.filter((d) => d.uploaded).length;
	const totalCount = documentsWithStatus.length;
	const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

	// Check if basic info is completed
	// Use phaseTwoStatuses if available, otherwise fallback to manual check
	const basicInfoCompleted = record.phaseTwoStatuses?.progress?.basic_info?.status
		? record.phaseTwoStatuses.progress.basic_info.status === 'completed' ||
		  record.phaseTwoStatuses.progress.basic_info.status === 'approved'
		: !!(
				record.gender &&
				record.dateOfBirth &&
				record.addressLine1 &&
				record.city &&
				record.state &&
				record.postcode
		  );

	const getStatusIcon = (completed: boolean) => {
		return completed ? (
			<CheckCircleIcon color="success" />
		) : (
			<PendingIcon sx={{ color: 'rgb(68, 99, 136)' }} />
		);
	};

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			{/* Back Button */}
			<Box sx={{ mb: 3 }}>
				<Button onClick={onBack} className={style.BackButton}>
					Back
				</Button>
			</Box>

			{/* Employee Info Header */}
			<Paper sx={{ p: 3, mb: 3 }}>
				<Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
					Onboarding Progress: {record.firstName} {record.lastName}
				</Typography>
				<Grid container spacing={3}>
<Grid item xs={12} md={4}>
<Typography variant="caption" color="text.secondary">
Email
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{record.email}
</Typography>
</Grid>
<Grid item xs={12} md={4}>
<Typography variant="caption" color="text.secondary">
Phone
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{record.cellPhone}
</Typography>
</Grid>
<Grid item xs={12} md={4}>
<Typography variant="caption" color="text.secondary">
Employment Start Date
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{record.employmentStartDate 
? new Date(record.employmentStartDate).toLocaleDateString('en-US', {
year: 'numeric',
month: 'short',
day: 'numeric'
})
: 'N/A'}
</Typography>
</Grid>
<Grid item xs={12} md={3}>
<Typography variant="caption" color="text.secondary">
Type
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{capitalizeWords(record.type)}
</Typography>
</Grid>
<Grid item xs={12} md={3}>
<Typography variant="caption" color="text.secondary">
Status
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{capitalizeWords(record.status)}
</Typography>
</Grid>
<Grid item xs={12} md={3}>
<Typography variant="caption" color="text.secondary">
Pay Type
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{capitalizeWords(record.payType)}
</Typography>
</Grid>
<Grid item xs={12} md={3}>
<Typography variant="caption" color="text.secondary">
Employment Type
</Typography>
<Typography variant="body1" sx={{ fontWeight: 500 }}>
{capitalizeWords(record.employmentType)}
</Typography>
</Grid>
</Grid>
			</Paper>

			{/* Phase 2: Employee Information & Documents */}
			{(basicInfoCompleted || documentsWithStatus.length > 0) && (
				<Paper sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
						Phase 2: Employee Submitted Information
					</Typography>
					
					{/* Overall Progress Status */}
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
						{getStatusIcon(basicInfoCompleted)}
						<Typography variant="body1" sx={{ ml: 2, fontWeight: 600 }}>
							Basic Information Status
						</Typography>
						<Box sx={{ ml: 'auto' }}>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{basicInfoCompleted ? 'Completed' : 'Pending'}
						</Typography>
					</Box>
					</Box>
					
					{basicInfoCompleted && (
						<>
					<Grid container spacing={3}>
						{/* Personal Information */}
						<Grid item xs={12}>
							<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
								Personal Information
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								First Name
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.firstName || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Middle Name
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.middleName || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Last Name
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.lastName || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Cell Phone
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.cellPhone || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Gender
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.gender || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Marital Status
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.maritalStatus || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Date of Birth
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.dateOfBirth 
									? new Date(record.dateOfBirth).toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'long',
										day: 'numeric'
									})
									: 'Not Provided'}
							</Typography>
						</Grid>

						{/* Address Information */}
						<Grid item xs={12} sx={{ mt: 2 }}>
							<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
								Address Information
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="caption" color="text.secondary">
								Address Line 1
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.addressLine1 || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="caption" color="text.secondary">
								Address Line 2
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.addressLine2 || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								City
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.city || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								State
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.state || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Postal Code
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.postcode || 'Not Provided'}
							</Typography>
						</Grid>
						<Grid item xs={12} md={4}>
							<Typography variant="caption" color="text.secondary">
								Country
							</Typography>
							<Typography variant="body1" sx={{ fontWeight: 500 }}>
								{record.country || 'Not Provided'}
							</Typography>
						</Grid>
					</Grid>
						</>
					)}

					{/* Documents Section */}
					<Box sx={{ mt: basicInfoCompleted ? 4 : 0 }}>
				<Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
					Submitted Documents
				</Typography>

				{/* Document Progress Bar */}
				<Box sx={{ mb: 3 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
						<Typography variant="body2" color="text.secondary">
							Documents: {completedCount} of {totalCount} completed
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{progressPercent}%
						</Typography>
					</Box>
					<LinearProgress
						variant="determinate"
						value={progressPercent}
						sx={{ height: 8, borderRadius: 4 }}
					/>
				</Box>

				{documentsWithStatus.length === 0 ? (
					<Box sx={{ textAlign: 'center', py: 4 }}>
						<Typography color="text.secondary">No documents configured</Typography>
					</Box>
				) : (
					<List>
						{documentsWithStatus.map((doc, idx) => (
							<React.Fragment key={doc.template.id}>
								<ListItem
									sx={{
										display: 'flex',
										alignItems: 'center',
										py: 2,
									}}
								>
									<ListItemIcon>{getStatusIcon(doc.uploaded)}</ListItemIcon>
									<ListItemText
										primary={doc.template.name}
										secondary={
											<>
												{doc.template.type}
												{doc.status && doc.status !== 'pending' && (
													<Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic' }}>
														({doc.status})
													</Typography>
												)}
											</>
										}
									/>
									<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{doc.uploaded ? 'Completed' : 'Pending'}
							</Typography>
										{doc.uploaded && doc.uploadedDocument && (
											<MuiButton
												variant="contained"
												size="small"
												startIcon={<DownloadIcon />}
												onClick={() => handleDownloadDocument(doc.uploadedDocument!)}
												sx={{ 
													ml: 2,
													backgroundColor: 'rgb(68, 99, 136)',
													color: '#fff',
													'&:hover': {
														backgroundColor: 'rgb(58, 89, 126)'
													}
												}}
											>
												Download
											</MuiButton>
										)}
									</Box>
								</ListItem>
								{idx < documentsWithStatus.length - 1 && <Divider />}
							</React.Fragment>
						))}
					</List>
				)}
			</Box>
			</Paper>
		)}

		{/* Manager Review Actions - Only show if Phase 2 is complete but not yet reviewed */}
		{record.phase2CompletedAt && !record.phase2ReviewedAt && (
			<Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
				<Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'rgb(68, 99, 136)' }}>
					Manager Review Actions
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Review the employee&apos;s submitted information and documents, then take an action below.
				</Typography>
				<Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
					<MuiButton
						variant="outlined"
						color="warning"
						onClick={handleReset}
						disabled={isProcessing}
					>
						Reset to Phase 2
					</MuiButton>
					<MuiButton
						variant="outlined"
						color="error"
						onClick={handleReject}
						disabled={isProcessing}
					>
						Reject
					</MuiButton>
					<MuiButton
						variant="contained"
						color="success"
						onClick={handleApprove}
						disabled={isProcessing}
					>
						{isProcessing ? 'Processing...' : 'Approve'}
					</MuiButton>
				</Box>
			</Paper>
		)}

		{/* Phase 3 Complete Message */}
			{record.phase3CompletedAt && (
				<Paper sx={{ p: 3, backgroundColor: '#e8f5e9' }}>
					<Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
						✓ Onboarding Complete
					</Typography>
					<Typography variant="body2" color="text.secondary">
						This onboarding was reviewed and completed on {new Date(record.phase3CompletedAt).toLocaleDateString()}.
					</Typography>
				</Paper>
			)}
		</Box>
	);
}
